import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { LiveClock, StatusIndicator } from '../components/LiveClock';

function StarField() {
  const stars = useMemo(() => Array.from({ length: 180 }).map((_, i) => ({
    id: i,
    top: Math.random() * 100,
    left: Math.random() * 100,
    size: Math.random() * 1.8 + 0.4,
    delay: Math.random() * 4,
    color: Math.random() > 0.92 ? (Math.random() > 0.5 ? '#e8a55a' : '#7eb8b8') : '#f3f1eb',
    opacity: Math.random() * 0.7 + 0.3,
  })), []);

  return (
    <div className="absolute inset-0 overflow-hidden">
      {stars.map((s) => (
        <div
          key={s.id}
          className="absolute rounded-full star"
          style={{
            top: `${s.top}%`,
            left: `${s.left}%`,
            width: `${s.size}px`,
            height: `${s.size}px`,
            backgroundColor: s.color,
            opacity: s.opacity,
            animationDelay: `${s.delay}s`,
            boxShadow: s.size > 1.5 ? `0 0 ${s.size * 2}px ${s.color}` : 'none',
          }}
        />
      ))}
      {/* Decorative cluster top-right (orange + asterisk) */}
      <div className="absolute" style={{ top: '28%', right: '12%' }}>
        <div className="flex items-center gap-1">
          <span className="text-white text-xs">*</span>
          <span className="text-[#e8a55a] text-[10px]">○</span>
        </div>
      </div>
      {/* Decorative cluster bottom-right (teal + star) */}
      <div className="absolute" style={{ bottom: '18%', right: '22%' }}>
        <div className="flex items-center gap-2">
          <span className="text-[#7eb8b8] text-xs">☆</span>
          <span className="text-[#a8d8e8] text-[10px]">○</span>
        </div>
      </div>
      {/* Cross/plus markers */}
      <div className="absolute text-white/70 text-xs" style={{ top: '52%', left: '14%' }}>╋</div>
      <div className="absolute text-white/40 text-[10px]" style={{ top: '54%', left: '16%' }}>+</div>
    </div>
  );
}

function ShootingStars() {
  return (
    <>
      <div className="shooting-star" style={{ top: '15%', right: '20%', animationDelay: '2s' }} />
      <div className="shooting-star" style={{ top: '75%', right: '40%', animationDelay: '7s' }} />
    </>
  );
}

export default function IntroPage() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShow(true), 200);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="relative min-h-screen w-full overflow-hidden" style={{ background: 'radial-gradient(ellipse at center, #1d1d1f 0%, #0f0f10 100%)' }}>
      <StarField />
      <ShootingStars />

      <div className="absolute top-10 left-1/2 -translate-x-1/2 text-white/40 text-xs">
        <span className="sparkle">⊹</span> ￣ <span className="sparkle" style={{ animationDelay: '1.2s' }}>˖</span>
      </div>

      <div className={`relative z-10 flex flex-col items-center justify-center min-h-screen px-4 transition-all duration-1000 ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <p className={`text-[10px] sm:text-[11px] tracking-[0.25em] uppercase text-white/55 ${show ? 'fade-slide-in' : 'opacity-0'}`}>Welcome to</p>
        <h1 className={`text-white text-[44px] sm:text-[60px] md:text-[50px] leading-none mt-3 sm:mt-4 tracking-tight text-center px-2 ${show ? 'fade-slide-in stagger-2' : 'opacity-0'}`}>
          Manisha&rsquo;s World
        </h1>

        <Link
          to="/onboarding?from=intro"
          className={`btn-magnetic btn-sheen mt-5 inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#f3f1eb] text-inkwell-900 text-[11px] uppercase tracking-wider hover:bg-white shadow-lg group ${show ? 'fade-slide-in stagger-3' : 'opacity-0'}`}
        >
          Explore <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
        </Link>
        <Link to="/home" className={`mt-4 text-[10px] uppercase tracking-[0.2em] text-white/45 hover:text-white/80 transition-colors ${show ? 'fade-slide-in stagger-4' : 'opacity-0'}`}>Skip Intro</Link>

        <div className={`mt-20 text-center text-[11px] tracking-wider text-white/55 leading-relaxed max-w-md ${show ? 'fade-slide-in stagger-5' : 'opacity-0'}`}>
          <span className="text-white/85">DISCOVERIES</span> ARE OUT THERE, WAITING
          <br />
          TO BE MADE. WHY NOT <span className="text-white/85">BY YOU?</span>
        </div>
      </div>

      <div className="absolute bottom-5 left-6 z-10">
        <LiveClock className="text-[11.5px] text-white/60 block" />
        <StatusIndicator className="mt-1 text-white/40" />
      </div>
    </div>
  );
}
